import { ArrowDropDownCircle, BlockOutlined } from '@mui/icons-material';
import {
    Button,
    Card,
    Collapse,
    IconButton,
    ListItemButton,
    ListItemText,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ListPageLayout from '../../../components/ListPageLayout';
import useDialog from '../../../hooks/useDialog';
import { useFetch } from '../../../hooks/useFecth';
import { useSearchTextInput } from '../../../hooks/useSearchTextInput';
import server from '../../../services/server';
import DialogCategory from './components/DialogCategory';

const MAX_LEVEL = 3;

function childrenOf(item) {
    return (item?.SubCategorys || []).filter(child => !child.deletedAt);
}

function CategoryNode({ item, level, opened, toggle }) {
    const kids = childrenOf(item);
    const canExpand = level < MAX_LEVEL && kids.length > 0;
    const isOpen = Boolean(opened[item.id]);

    return (
        <Box>
            <ListItemButton
                component={Link}
                to={`#category|${item.id}`}
                dense={level > 1}
                sx={{ py: 1, pl: 1 + (level - 1) * 2, borderRadius: 1 }}
            >
                <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{ fontWeight: level === 1 ? 600 : 500 }}
                    secondary={canExpand ? `${kids.length} ${kids.length === 1 ? 'filha' : 'filhas'}` : null}
                />
                {canExpand && (
                    <IconButton
                        size="small"
                        aria-label={isOpen ? 'Recolher filhas' : 'Ver filhas'}
                        onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggle(item.id);
                        }}
                    >
                        <ArrowDropDownCircle
                            sx={{
                                transform: isOpen ? 'rotate(180deg)' : 'none',
                                transition: 'transform .2s',
                            }}
                        />
                    </IconButton>
                )}
            </ListItemButton>
            {canExpand && (
                <Collapse in={isOpen}>
                    <Stack spacing={0.25} pb={0.5}>
                        {kids.map(child => (
                            <CategoryNode
                                key={child.id}
                                item={child}
                                level={level + 1}
                                opened={opened}
                                toggle={toggle}
                            />
                        ))}
                    </Stack>
                </Collapse>
            )}
        </Box>
    );
}

export default function Category() {
    const { search, input, handleChange } = useSearchTextInput();
    const { data, mutate, isFetching } = useFetch('category', {
        params: { name: search.trim() || undefined },
    });
    useDialog('#category');
    const [opened, setOpened] = useState({});

    const toggle = id => {
        setOpened(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const migrationCategory = async () => {
        await server.post(`migration/category`);
        await mutate();
    };

    const rows = Array.isArray(data) ? data : [];

    return (
        <>
            <ListPageLayout
                title="Categorias"
                subtitle="Categorias pai e filhas dos anunciantes e ofertas. Expanda até o 3º nível para editar."
                counts={[{ label: 'categorias', value: rows.length, color: 'primary' }]}
                loading={isFetching && !data}
                showViewToggle={false}
                filterActive={Boolean(search.trim())}
                onClearFilters={search.trim() ? () => handleChange('') : null}
                actions={
                    <>
                        <Button size="small" variant="outlined" onClick={migrationCategory}>
                            Migrar
                        </Button>
                        <Button size="small" variant="contained" component={Link} to="#category">
                            Adicionar
                        </Button>
                    </>
                }
                filterPanel={({ close }) => (
                    <Stack
                        spacing={2}
                        component="form"
                        onSubmit={e => {
                            e.preventDefault();
                            close?.();
                        }}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            label="Filtrar categoria pai"
                            value={input}
                            onChange={e => handleChange(e.target.value)}
                        />
                        <Button type="submit" variant="contained" fullWidth>
                            Aplicar
                        </Button>
                    </Stack>
                )}
                isEmpty={!isFetching && rows.length === 0}
                empty={
                    <Stack direction="row" justifyContent="center" gap={1}>
                        <Typography>Nenhum item listado.</Typography>
                        <BlockOutlined />
                    </Stack>
                }
            >
                <Stack spacing={1} p={2}>
                    {rows.map(item => (
                        <Card key={item.id} variant="outlined">
                            <CategoryNode item={item} level={1} opened={opened} toggle={toggle} />
                        </Card>
                    ))}
                </Stack>
            </ListPageLayout>
            <DialogCategory mutate={mutate} />
        </>
    );
}
